import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  Plus,
  X,
  MapPin,
  Clock,
  DollarSign,
  Building,
  FileText,
  GraduationCap,
  Camera,
  Upload,
  Trash2,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  AlertCircle,
  Search,
} from "lucide-react";
import api from "../../../utils/Api";

// InputField component
const InputField = ({ label, error, required, children }) => (
  <div className="space-y-2">
    {label && (
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
    )}
    {children}
    {error && (
      <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
        <AlertCircle className="w-4 h-4" />
        {error}
      </p>
    )}
  </div>
);

const EditMissionPage = () => {
  const { t, i18n } = useTranslation("createMission");
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Job Title Autocomplete & Sector
  const [availableJobs, setAvailableJobs] = useState([]);
  const [availableSectors, setAvailableSectors] = useState([]);
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [filteredJobs, setFilteredJobs] = useState([]);

  // States for wilayas and cities
  const [wilayas, setWilayas] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingWilayas, setLoadingWilayas] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    company: "",
    location: {
      line1: "",
      line2: "",
      zip_code: "", // Note: backend uses zipCode sometimes, check consistency. NewMission uses zip_code.
      city: "",
      wilaya: "",
      coordinates: [0, 0],
    },
    salaryHour: "",
    schedule: [{ date: "", startTime: "", endTime: "" }],
    skills: [],
    requirements: "",
    educationLevel: "",
    imageFile: null,
    imagePreview: "",
    status: "active",
    sector: ""
  });

  const [errors, setErrors] = useState({});
  const [skillInput, setSkillInput] = useState("");

  const educationLevels = [
    "aucun",
    "CAP/BEP",
    "Bac",
    "Bac+2",
    "Bac+3",
    "Bac+5",
    "Doctorat",
  ];

  const steps = [
    { id: 1, name: t("add.steps.basic"), icon: Building },
    { id: 2, name: t("add.steps.location"), icon: MapPin },
    { id: 3, name: t("add.steps.schedule"), icon: Clock },
    { id: 4, name: t("add.steps.details"), icon: FileText },
  ];

  // Fetch initial data
  useEffect(() => {
    const init = async () => {
      await Promise.all([
        fetchWilayas(),
        fetchJobsAndSectors()
      ]);
      fetchMission();
    };
    init();
  }, [id]);

  // Filter jobs logic
  useEffect(() => {
    if (formData.title && !selectedJob) {
      const filtered = availableJobs.filter(job => {
        const jobTitle = job.title || '';
        return jobTitle.toLowerCase().includes(formData.title.toLowerCase());
      });
      setFilteredJobs(filtered.slice(0, 10));
    } else {
      setFilteredJobs([]);
    }
  }, [formData.title, availableJobs, selectedJob]);

  const fetchWilayas = async () => {
    setLoadingWilayas(true);
    try {
      const response = await api.get("/data/wilayas");
      setWilayas(response.data);
      return response.data; // Return for chaining
    } catch (err) {
      console.error("Error fetching wilayas:", err);
      return [];
    } finally {
      setLoadingWilayas(false);
    }
  };

  const fetchCities = async (wilayaCode) => {
    setLoadingCities(true);
    try {
      const response = await api.get(`/data/cities/wilaya/${wilayaCode}`);
      setCities(response.data);
      return response.data;
    } catch (err) {
      console.error("Error fetching cities:", err);
      return [];
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchJobsAndSectors = async () => {
    try {
      const [jobsRes, sectorsRes] = await Promise.all([
        api.get("/job/"),
        api.get("/sector/")
      ]);
      setAvailableJobs(jobsRes.data);
      setAvailableSectors(sectorsRes.data);
    } catch (err) {
      console.error("Error fetching jobs/sectors:", err);
    }
  };

  const fetchMission = async () => {
    try {
      setLoading(true);
      const response = await api.get("/mission/" + id);
      const mission = response.data.data;

      // Handle Title (Object vs String)
      let titleStr = "";
      let sectorId = "";
      if (typeof mission.title === 'object') {
        titleStr = mission.title?.title || "";
        // Pre-select the job if it exists as an object
        if (mission.title && mission.title._id) {
          setSelectedJob(mission.title);
        }
      } else {
        titleStr = mission.title || "";
      }

      // Handle Location (Wilaya/City Name to Code mapping)
      // The backend returns NAMES now (e.g. "Alger"). We need CODES for the dropdowns.
      // We need to wait for wilayas to be loaded or fetch them here.
      // Since we run this in init() after fetchWilayas, we should have them in state? 
      // Wait, Promise.all doesn't guarantee state update visibility immediately if using state from closure.
      // Better to fetch fresh wilayas inside here or reuse the response if possible.
      // Actually, let's just fetch them again or use the cached response if we modify the structure.
      // For simplicity, I'll re-read the wilayas response.
      const wilayasRes = await api.get("/data/wilayas");
      const loadedWilayas = wilayasRes.data;

      let wilayaCode = "";
      let cityId = "";
      let zipCode = mission.location?.zipCode || mission.location?.zip_code || "";

      if (mission.location?.wilaya) {
        // Try to find by Name (FR or EN or AR?)
        // Backend returns name_fr usually.
        const foundWilaya = loadedWilayas.find(w =>
          w.name_fr === mission.location.wilaya ||
          w.name_en === mission.location.wilaya ||
          w.code == mission.location.wilaya // In case it IS a code
        );
        if (foundWilaya) {
          wilayaCode = foundWilaya.code;

          // Fetch cities for this wilaya to find the city ID
          const citiesRes = await api.get(`/data/cities/${wilayaCode}`);
          const loadedCities = citiesRes.data;
          setCities(loadedCities);

          if (mission.location?.city) {
            const foundCity = loadedCities.find(c =>
              c.name === mission.location.city ||
              c.name_ar === mission.location.city ||
              c.id == mission.location.city
            );
            if (foundCity) {
              cityId = foundCity.id;
              if (!zipCode) zipCode = foundCity.postalCode;
            }
          }
        }
      }

      setFormData({
        title: titleStr,
        description: mission.description || "",
        company: mission.company || "",
        location: {
          line1: mission.location?.line1 || "",
          line2: mission.location?.line2 || "",
          zip_code: zipCode,
          city: cityId, // Set ID
          wilaya: wilayaCode, // Set Code
          coordinates: mission.location?.coordinates || [0, 0],
        },
        salaryHour: mission.salaryHour || "",
        schedule: mission.schedule?.length > 0 ? mission.schedule : [{ date: "", startTime: "", endTime: "" }],
        skills: mission.skills || [],
        requirements: mission.requirements || "",
        educationLevel: mission.educationLevel || "",
        // Image
        imageFile: null,
        imagePreview: mission.imageUrl ? (mission.imageUrl.startsWith('http') ? mission.imageUrl : process.env.REACT_APP_BASE_URL + mission.imageUrl) : "",
        status: mission.status || "active",
        sector: mission.sector || "" // Populate if available
      });

    } catch (error) {
      console.error("Error fetching mission:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectJob = (job) => {
    setSelectedJob(job);
    setFormData(prev => ({ ...prev, title: job.title, sector: '' }));
    setShowJobDropdown(false);
  };

  const handleInputChange = (field, value) => {
    // Flattened location handling in update? No, NewMission uses nested location state updates.
    if (field.startsWith("location.")) {
      const locationField = field.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        location: { ...prev.location, [locationField]: value },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleWilayaChange = async (wilayaCode) => {
    handleInputChange("location.wilaya", wilayaCode);
    handleInputChange("location.city", "");
    handleInputChange("location.zip_code", "");

    if (wilayaCode) {
      await fetchCities(wilayaCode);
    } else {
      setCities([]);
    }
  };

  const handleCityChange = (cityName) => {
    const selectedCity = cities.find((city) => city.name === cityName);
    handleInputChange("location.city", cityName);
    handleInputChange("location.zip_code", selectedCity?.postalCode || formData.location.zip_code);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setErrors((prev) => ({ ...prev, imageFile: t("validation.image.type") }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, imageFile: t("validation.image.size") }));
        return;
      }
      const previewUrl = URL.createObjectURL(file);
      setFormData((prev) => ({ ...prev, imageFile: file, imagePreview: previewUrl }));
      setErrors((prev) => ({ ...prev, imageFile: "" }));
    }
  };

  const removeImage = () => {
    if (formData.imagePreview && formData.imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(formData.imagePreview);
    }
    setFormData((prev) => ({ ...prev, imageFile: null, imagePreview: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleScheduleChange = (index, field, value) => {
    const newSchedule = [...formData.schedule];
    newSchedule[index][field] = value;
    setFormData((prev) => ({ ...prev, schedule: newSchedule }));
  };

  const addScheduleSlot = () => {
    setFormData((prev) => ({
      ...prev,
      schedule: [...prev.schedule, { date: "", startTime: "", endTime: "" }],
    }));
  };

  const removeScheduleSlot = (index) => {
    setFormData((prev) => ({
      ...prev,
      schedule: prev.schedule.filter((_, i) => i !== index),
    }));
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()],
      }));
      setSkillInput("");
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.filter((skill) => skill !== skillToRemove),
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.title.trim()) newErrors.title = t("validation.required");

      const exactMatch = availableJobs.find(job => job.title.toLowerCase() === formData.title.toLowerCase());
      if (!exactMatch && !formData.sector) {
        // Maybe not enforce sector on EDIT unless they change title to something new?
        // For now, let's keep it consistent.
        newErrors.sector = "Veuillez sélectionner un secteur pour ce nouveau type de mission";
      }

      if (!formData.description.trim()) newErrors.description = t("validation.required");
    }

    if (step === 2) {
      if (!formData.location.wilaya) newErrors["location.wilaya"] = t("validation.required");
      if (!formData.location.city) newErrors["location.city"] = t("validation.required");
      if (!formData.location.zip_code) newErrors["location.zip_code"] = t("validation.required");
      if (!formData.salaryHour || formData.salaryHour <= 0) newErrors.salaryHour = t("validation.required");
    }

    if (step === 3) {
      formData.schedule.forEach((slot, index) => {
        if (!slot.date) newErrors[`schedule.${index}.date`] = t("validation.required");
        if (!slot.startTime) newErrors[`schedule.${index}.startTime`] = t("validation.required");
        if (!slot.endTime) newErrors[`schedule.${index}.endTime`] = t("validation.required");
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === "imageFile" || key === "imagePreview" || key === "company") return;
        if (key === "title" && selectedJob) {
          formDataToSend.append("title", selectedJob._id);
          return;
        }

        if (key === "location") {
          // Send location with proper keys (zipCode vs zip_code consistency)
          // Backend usually expects 'zipCode' based on EditMission.js previous code?
          // Previous EditMission had 'zipCode'. NewMission has 'zip_code'.
          // I should verify backend model. 
          // `location: { zipCode: String }` usually in Mongoose.
          // I will ensure I send what backend needs. I'll map it.
          const loc = { ...formData.location };
          if (loc.zip_code) loc.zipCode = loc.zip_code; // Ensure both or correct one
          formDataToSend.append("location", JSON.stringify(loc));
        } else if (Array.isArray(formData[key])) {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (formData[key] !== null && formData[key] !== undefined) {
          formDataToSend.append(key, formData[key].toString());
        }
      });

      if (formData.imageFile) {
        formDataToSend.append("image", formData.imageFile);
      }

      await api.put("/mission/" + id, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      navigate("/dashboard/missions");
    } catch (error) {
      console.error("Error updating mission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t("loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">

        {/* Progress Stepper */}
        <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center gap-2 flex-1">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${isCompleted
                        ? "bg-primary-600 text-white"
                        : isActive
                          ? "bg-primary-600 text-white ring-4 ring-primary-100 dark:ring-primary-900"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                        }`}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="w-6 h-6" />
                      ) : (
                        <Icon className="w-6 h-6" />
                      )}
                    </div>
                    <span
                      className={`text-sm font-medium text-center ${isActive || isCompleted
                        ? "text-gray-900 dark:text-white"
                        : "text-gray-500 dark:text-gray-400"
                        }`}
                    >
                      {step.name}
                    </span>
                  </div>

                  {index < steps.length - 1 && (
                    <div
                      className={`h-0.5 flex-1 mx-4 transition-colors duration-300 ${currentStep > step.id
                        ? "bg-primary-600"
                        : "bg-gray-200 dark:bg-gray-700"
                        }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          <div className="p-6 sm:p-8">

            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <div className="space-y-6 animate-fade-in">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {t("add.basic.title")}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t("add.basic.subtitle")}
                  </p>
                </div>

                <InputField label={t("add.title.label")} error={errors.title} required>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => {
                        handleInputChange("title", e.target.value);
                        setSelectedJob(null);
                        setShowJobDropdown(true);
                      }}
                      onFocus={() => setShowJobDropdown(true)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors ${errors.title ? "border-red-500 focus:ring-red-500" : "border-gray-300"
                        }`}
                      placeholder={t("add.title.placeholder")}
                    />
                    {showJobDropdown && filteredJobs.length > 0 && !selectedJob && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {filteredJobs.map(job => (
                          <button
                            key={job._id}
                            type="button"
                            onClick={() => handleSelectJob(job)}
                            className="w-full px-4 py-3 text-left hover:bg-primary-50 dark:hover:bg-primary-900/20 transition flex items-center justify-between group"
                          >
                            <span className="text-gray-900 dark:text-white">{job.title}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {showJobDropdown && (
                      <div className="fixed inset-0 z-[5]" onClick={() => setShowJobDropdown(false)}></div>
                    )}
                  </div>

                  {formData.title && !availableJobs.some(j => j.title.toLowerCase() === formData.title.toLowerCase()) && (
                    <div className="mt-4 animate-fade-in">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Secteur d'activité (Nouveau type de mission) *
                      </label>
                      <input
                        type="text"
                        list="sector-list"
                        value={formData.sector}
                        onChange={(e) => handleInputChange("sector", e.target.value)}
                        placeholder="Sélectionnez ou tapez un secteur"
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors ${errors.sector ? 'border-red-500' : 'border-gray-300'
                          }`}
                      />
                      <datalist id="sector-list">
                        {availableSectors.map(sector => (
                          <option key={sector._id} value={sector.name} />
                        ))}
                      </datalist>
                      {errors.sector && (
                        <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 mt-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.sector}
                        </p>
                      )}
                    </div>
                  )}
                </InputField>

                <InputField label={t("add.description.label")} error={errors.description} required>
                  <textarea
                    rows={5}
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors resize-none ${errors.description ? "border-red-500 focus:ring-red-500" : "border-gray-300"
                      }`}
                    placeholder={t("add.description.placeholder")}
                  />
                </InputField>


              </div>
            )}

            {/* Step 2: Location */}
            {currentStep === 2 && (
              <div className="space-y-6 animate-fade-in">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {t("add.location.title")}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t("add.location.subtitle")}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField label={t("add.location.line1.label")}>
                    <input
                      type="text"
                      value={formData.location.line1}
                      onChange={(e) => handleInputChange("location.line1", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      placeholder={t("add.location.line1.placeholder")}
                    />
                  </InputField>

                  <InputField label={t("add.location.line2.label")}>
                    <input
                      type="text"
                      value={formData.location.line2}
                      onChange={(e) => handleInputChange("location.line2", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      placeholder={t("add.location.line2.placeholder")}
                    />
                  </InputField>

                  <div className="md:col-span-2">
                    <InputField label={t("add.location.wilaya.label")} error={errors["location.wilaya"]} required>
                      <select
                        value={formData.location.wilaya}
                        onChange={(e) => handleWilayaChange(e.target.value)}
                        disabled={loadingWilayas}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed ${errors["location.wilaya"] ? "border-red-500 focus:ring-red-500" : "border-gray-300"
                          }`}
                      >
                        <option value="">{t("add.location.wilaya.placeholder")}</option>
                        {wilayas?.map((wilaya) => (
                          <option key={wilaya.code} value={wilaya.code}>
                            {wilaya.code + " - " + (wilaya[`name_${i18n.language}`] || wilaya.name_fr)}
                          </option>
                        ))}
                      </select>
                    </InputField>
                  </div>

                  <InputField label={t("add.location.city.label")} error={errors["location.city"]} required>
                    <input
                      type="text"
                      list="edit-mission-cities-list"
                      value={formData.location.city}
                      onChange={(e) => handleCityChange(e.target.value)}
                      disabled={!formData.location.wilaya || loadingCities}
                      placeholder={!formData.location.wilaya ? "Sélectionnez d'abord une wilaya" : t("add.location.city.placeholder")}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed ${errors["location.city"] ? "border-red-500 focus:ring-red-500" : "border-gray-300"
                        }`}
                    />
                    <datalist id="edit-mission-cities-list">
                      {cities?.map((city) => (
                        <option key={city.id} value={city.name} />
                      ))}
                    </datalist>
                  </InputField>

                  <InputField label={t("add.location.zipCode.label")} error={errors["location.zip_code"]} required>
                    <input
                      type="text"
                      value={formData.location.zip_code}
                      onChange={(e) => handleInputChange("location.zip_code", e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors ${errors["location.zip_code"] ? "border-red-500 focus:ring-red-500" : "border-gray-300"
                        }`}
                      placeholder={t("add.location.zipCode.placeholder")}
                    />
                  </InputField>
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3 mb-6">
                    <DollarSign className="w-6 h-6 text-primary-600" />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {t("add.salary.title")}
                    </h3>
                  </div>

                  <div className="max-w-md">
                    <InputField label={t("add.salary.hourly")} error={errors.salaryHour} required>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.salaryHour}
                          onChange={(e) => handleInputChange("salaryHour", parseFloat(e.target.value))}
                          className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors ${errors.salaryHour ? "border-red-500 focus:ring-red-500" : "border-gray-300"
                            }`}
                          placeholder="450.00"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 font-medium">DA</span>
                      </div>
                    </InputField>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Schedule */}
            {currentStep === 3 && (
              <div className="space-y-6 animate-fade-in">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {t("add.schedule.title")}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t("add.schedule.subtitle")}
                  </p>
                </div>

                <div className="space-y-4">
                  {formData.schedule.map((slot, index) => (
                    <div key={index} className="p-5 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:border-primary-300 dark:hover:border-primary-700 transition-colors">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </div>
                          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                            {t("add.schedule.slot")} {index + 1}
                          </h3>
                        </div>
                        {formData.schedule.length > 1 && (
                          <button type="button" onClick={() => removeScheduleSlot(index)} className="text-red-500 hover:text-red-700">
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <InputField label={t("add.schedule.date")} error={errors[`schedule.${index}.date`]} required>
                          <input
                            type="date"
                            value={slot.date ? new Date(slot.date).toISOString().split('T')[0] : ""}
                            onChange={(e) => handleScheduleChange(index, "date", e.target.value)}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white transition-colors ${errors[`schedule.${index}.date`] ? "border-red-500 focus:ring-red-500" : "border-gray-300"
                              }`}
                          />
                        </InputField>
                        <InputField label={t("add.schedule.startTime")} error={errors[`schedule.${index}.startTime`]} required>
                          <input
                            type="time"
                            value={slot.startTime}
                            onChange={(e) => handleScheduleChange(index, "startTime", e.target.value)}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white transition-colors ${errors[`schedule.${index}.startTime`] ? "border-red-500 focus:ring-red-500" : "border-gray-300"
                              }`}
                          />
                        </InputField>
                        <InputField label={t("add.schedule.endTime")} error={errors[`schedule.${index}.endTime`]} required>
                          <input
                            type="time"
                            value={slot.endTime}
                            onChange={(e) => handleScheduleChange(index, "endTime", e.target.value)}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white transition-colors ${errors[`schedule.${index}.endTime`] ? "border-red-500 focus:ring-red-500" : "border-gray-300"
                              }`}
                          />
                        </InputField>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addScheduleSlot}
                    className="flex items-center justify-center w-full py-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-primary-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:border-primary-400 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-all font-medium"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    {t("add.schedule.add")}
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Details */}
            {currentStep === 4 && (
              <div className="space-y-6 animate-fade-in">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {t("add.skills.title")}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t("add.skills.subtitle")}
                  </p>
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                    placeholder={t("add.skills.placeholder")}
                  />
                  <button
                    type="button"
                    onClick={addSkill}
                    className="px-5 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    {t("add.skills.add")}
                  </button>
                </div>

                {formData.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.skills.map((skill, index) => (
                      <span key={index} className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                        {skill}
                        <button type="button" onClick={() => removeSkill(skill)} className="ml-2 hover:bg-primary-200 dark:hover:bg-primary-800 rounded p-0.5 transition-colors">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary-600" />
                    {t("add.requirements.title")}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputField label={t("add.requirements.description")}>
                      <textarea
                        rows={4}
                        value={formData.requirements}
                        onChange={(e) => handleInputChange("requirements", e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors resize-none"
                        placeholder={t("add.requirements.placeholder")}
                      />
                    </InputField>

                    <InputField label={t("add.education.level")}>
                      <select
                        value={formData.educationLevel}
                        onChange={(e) => handleInputChange("educationLevel", e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      >
                        <option value="">{t("add.education.select")}</option>
                        {educationLevels.map((level) => (
                          <option key={level} value={level}>
                            {t(`add.education.levels.${level}`)}
                          </option>
                        ))}
                      </select>
                    </InputField>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-8 mt-8 border-t border-gray-200 dark:border-gray-700">
              {currentStep > 1 ? (
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium flex items-center gap-2"
                >
                  <ChevronLeft className="w-5 h-5" />
                  {t("add.actions.previous")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/missions')}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                >
                  {t("edit.actions.cancel")}
                </button>
              )}

              {currentStep < steps.length ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex items-center gap-2 px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all font-medium"
                >
                  {t("add.actions.next")}
                  <ChevronRight className="w-5 h-5" />
                </button>
              ) : (
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      {t("edit.actions.saving")}
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      {t("edit.actions.save")}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditMissionPage;
